import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Tooltip,
  TableSortLabel,
  Menu,
  ListItemText,
  ListItemIcon,
  Chip,
  Stack,
} from "@mui/material";
import {
  Launch as LaunchIcon,
  Link as LinkIcon,
  Edit as EditIcon,  
} from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";
import {
  fetchAllEvents,
  fetchAllOrganizations,
  crmSliceSelector,
  updateEventOverridesInState,
} from "../../../redux/features/crmSlice";
import {
  statusForEvent,  
  updateEventOverrides,
} from "../../../redux/features/adminEventsSlice";
import { usersSliceSelector } from "../../../redux/features/usersSlice";
import EventStatusView from "../../AdminEvents/EventsList/EventListItem/components/EventStatusView";
import getSlug from "speakingurl";
import EventOverridesModal from "./EventOverridesModal";

export default function CRMEvents() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const {
    allEvents,
    allEventsLoading,
    allEventsLastVisible,
    allEventsAllLoaded,
    allEventsFilter: currentFilter,
    allEventStats,
    organizationsMap,
    organizationsFetched,
  } = crmState;
  const usersState = useSelector((state) => usersSliceSelector(state));
  const isSuperAdmin = usersState?.user?.superAdmin === true;

  // Parse event filter from URL (upcoming/draft/past)
  const eventFilter = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("filter") || "upcoming";
  }, [location.search]);

  // Parse sort params from URL query string
  const sortParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      sortBy: searchParams.get("sortBy") || "startsAt",
      sortOrder: searchParams.get("sortOrder") || "asc",
    };
  }, [location.search]);

  // Parse filter params from URL query string
  const filterParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const filters = { include: {}, exclude: {} };

    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_include_")) {
        const field = key.replace("filter_include_", "");
        filters.include[field] = value.split(",").filter(Boolean);
      } else if (key.startsWith("filter_exclude_")) {
        const field = key.replace("filter_exclude_", "");
        filters.exclude[field] = value.split(",").filter(Boolean);
      }
    });

    return filters;
  }, [location.search]);

  const observerTarget = useRef(null);
  const fetchAttemptedRef = useRef(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuField, setContextMenuField] = useState(null);
  const [editingEventId, setEditingEventId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [modalHideFromPublicLists, setModalHideFromPublicLists] =
    useState(false);
  const [modalHideEvent, setModalHideEvent] = useState(false);
  const [contextMenuValue, setContextMenuValue] = useState(null);
  const [chipContextMenu, setChipContextMenu] = useState(null);
  const [chipContextMenuData, setChipContextMenuData] = useState(null);

  // Fetch organizations if needed
  useEffect(() => {
    if (!organizationsFetched) {
      dispatch(fetchAllOrganizations());
    }
  }, [organizationsFetched, dispatch]);

  // Track previous params to detect changes
  const prevEventFilterRef = useRef(eventFilter);
  const prevPathnameRef = useRef(location.pathname);
  const prevSearchRef = useRef(location.search);

  // Helper functions for filtering
  const getFirestoreFieldName = (columnName) => {
    const fieldMap = {
      organizationId: "organizationId",
      status: "status",
    };
    return fieldMap[columnName] || null;
  };

  const getEventStatus = useCallback(
    (event) => {
      // Merge stats into event for statusForEvent
      let endsAtTimestamp = event.endsAt;
      if (endsAtTimestamp && !endsAtTimestamp.toMillis) {
        if (endsAtTimestamp.seconds !== undefined) {
          endsAtTimestamp = new Timestamp(
            endsAtTimestamp.seconds,
            endsAtTimestamp.nanoseconds || 0,
          );
        }
      }
      const eventWithStats = {
        ...event,
        endsAt: endsAtTimestamp,
        stats: allEventStats[event.id],
      };
      const result = statusForEvent(eventWithStats);
      return result?.status ?? "unknown";
    },
    [allEventStats],
  );

  const getDisplayValue = (field, rawValue) => {
    if (field === "organizationId") {
      const org = organizationsMap?.[rawValue];
      return org?.name || rawValue;
    }
    if (field === "status") {
      // Convert status to display value
      const statusMap = {
        draft: "Draft",
        pending: "Pending",
        onSale: "On Sale",
        soldOut: "Sold Out",
        ended: "Ended",
      };
      return statusMap[rawValue] || rawValue;
    }
    return rawValue;
  };

  const toggleFilter = (field, value, type, isRawValue = false) => {
    const searchParams = new URLSearchParams(location.search);
    const includeKey = `filter_include_${field}`;
    const excludeKey = `filter_exclude_${field}`;

    if (type === "include") {
      const currentInclude = filterParams.include[field] || [];
      const newInclude = currentInclude.includes(value)
        ? currentInclude.filter((v) => v !== value)
        : [...currentInclude, value];

      if (newInclude.length > 0) {
        searchParams.set(includeKey, newInclude.join(","));
      } else {
        searchParams.delete(includeKey);
      }
      // Remove from exclude if present
      const currentExclude = filterParams.exclude[field] || [];
      if (currentExclude.includes(value)) {
        const newExclude = currentExclude.filter((v) => v !== value);
        if (newExclude.length > 0) {
          searchParams.set(excludeKey, newExclude.join(","));
        } else {
          searchParams.delete(excludeKey);
        }
      }
    } else {
      const currentExclude = filterParams.exclude[field] || [];
      const newExclude = currentExclude.includes(value)
        ? currentExclude.filter((v) => v !== value)
        : [...currentExclude, value];

      if (newExclude.length > 0) {
        searchParams.set(excludeKey, newExclude.join(","));
      } else {
        searchParams.delete(excludeKey);
      }
      // Remove from include if present
      const currentInclude = filterParams.include[field] || [];
      if (currentInclude.includes(value)) {
        const newInclude = currentInclude.filter((v) => v !== value);
        if (newInclude.length > 0) {
          searchParams.set(includeKey, newInclude.join(","));
        } else {
          searchParams.delete(includeKey);
        }
      }
    }

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const clearFilter = (field, value, type) => {
    toggleFilter(field, value, type, true);
  };

  const clearAllFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    const keysToDelete = [];
    searchParams.forEach((value, key) => {
      if (
        key.startsWith("filter_include_") ||
        key.startsWith("filter_exclude_")
      ) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => searchParams.delete(key));
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleCellClick = (event, field, rawValue, displayValue) => {
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setContextMenuField(field);
    setContextMenuValue({ rawValue, displayValue });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setContextMenuField(null);
    setContextMenuValue(null);
  };

  const handleFilterBy = () => {
    if (contextMenuField && contextMenuValue) {
      const filterField = getFirestoreFieldName(contextMenuField);
      if (filterField) {
        const filterValue =
          contextMenuValue.rawValue || contextMenuValue.displayValue;
        const isRawValue = !!contextMenuValue.rawValue;
        toggleFilter(filterField, filterValue, "include", isRawValue);
      }
    }
    handleContextMenuClose();
  };

  const handleExclude = () => {
    if (contextMenuField && contextMenuValue) {
      const filterField = getFirestoreFieldName(contextMenuField);
      if (filterField) {
        const filterValue =
          contextMenuValue.rawValue || contextMenuValue.displayValue;
        const isRawValue = !!contextMenuValue.rawValue;
        toggleFilter(filterField, filterValue, "exclude", isRawValue);
      }
    }
    handleContextMenuClose();
  };

  const handleChipClick = (event, field, value, currentType) => {
    event.stopPropagation();
    setChipContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setChipContextMenuData({ field, value, currentType });
  };

  const handleChipContextMenuClose = () => {
    setChipContextMenu(null);
    setChipContextMenuData(null);
  };

  const handleFlipFilter = () => {
    if (chipContextMenuData) {
      const { field, value, currentType } = chipContextMenuData;
      const newType = currentType === "include" ? "exclude" : "include";
      toggleFilter(field, value, newType, true);
    }
    handleChipContextMenuClose();
  };

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    Object.values(filterParams.include || {}).forEach((values) => {
      count += values.length;
    });
    Object.values(filterParams.exclude || {}).forEach((values) => {
      count += values.length;
    });
    return count;
  }, [filterParams]);

  // Helper component for filterable table cell
  const FilterableCell = ({ field, value, rawValue, children, ...props }) => {
    const firestoreField = getFirestoreFieldName(field);

    // Don't allow filtering on name, tickets sold, or revenue
    const nonFilterableFields = ["name", "ticketsSold", "revenue"];
    if (!firestoreField || nonFilterableFields.includes(field)) {
      return <TableCell {...props}>{children}</TableCell>;
    }

    const isFilterable =
      value !== "N/A" &&
      rawValue !== null &&
      rawValue !== undefined &&
      rawValue !== "N/A";

    const hasFilter =
      filterParams.include[firestoreField]?.length > 0 ||
      filterParams.exclude[firestoreField]?.length > 0;

    return (
      <TableCell
        {...props}
        onClick={
          isFilterable
            ? (e) => handleCellClick(e, field, rawValue, value)
            : undefined
        }
        sx={{
          cursor: isFilterable ? "pointer" : "default",
          position: "relative",
          ...(isFilterable && {
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }),
          ...(hasFilter && {
            backgroundColor: "action.selected",
          }),
          ...props.sx,
        }}
      >
        {children}
      </TableCell>
    );
  };

  const eventsList = Object.values(allEvents || {});

  // Apply client-side filtering for organization and status
  let filteredEvents = eventsList;

  // Filter by organization
  if (filterParams.include?.organizationId?.length > 0) {
    filteredEvents = filteredEvents.filter((event) =>
      filterParams.include.organizationId.includes(event.organizationId),
    );
  }
  if (filterParams.exclude?.organizationId?.length > 0) {
    filteredEvents = filteredEvents.filter(
      (event) =>
        !filterParams.exclude.organizationId.includes(event.organizationId),
    );
  }

  // Filter by status
  if (filterParams.include?.status?.length > 0) {
    filteredEvents = filteredEvents.filter((event) => {
      const status = getEventStatus(event);
      return filterParams.include.status.includes(status);
    });
  }
  if (filterParams.exclude?.status?.length > 0) {
    filteredEvents = filteredEvents.filter((event) => {
      const status = getEventStatus(event);
      return !filterParams.exclude.status.includes(status);
    });
  }

  // Fetch initial events when filter changes or on mount
  useEffect(() => {
    const eventFilterChanged = prevEventFilterRef.current !== eventFilter;
    const pathnameChanged = prevPathnameRef.current !== location.pathname;

    // Reset fetch attempts when filter changes
    if (eventFilterChanged || pathnameChanged) {
      fetchAttemptedRef.current = false;
    }

    // Update refs
    prevEventFilterRef.current = eventFilter;
    prevPathnameRef.current = location.pathname;
    prevSearchRef.current = location.search;

    // Fetch if filter changed or if we haven't fetched yet (no events and not loading)
    const hasNoEvents = Object.keys(allEvents || {}).length === 0;
    const shouldFetch =
      eventFilter !== currentFilter ||
      (hasNoEvents && !allEventsLoading && !fetchAttemptedRef.current);

    if (shouldFetch) {
      fetchAttemptedRef.current = true;
      dispatch(
        fetchAllEvents({
          filter: eventFilter,
          pageSize: 20,
          reset: true,
        }),
      );
    }
  }, [
    eventFilter,
    currentFilter,
    allEvents,
    allEventsLoading,
    dispatch,
    location.pathname,
    location.search,
  ]);

  // Load more events when scrolling to bottom
  const loadMore = useCallback(() => {
    if (
      !allEventsLoading &&
      !allEventsAllLoaded &&
      allEventsLastVisible &&
      eventFilter === currentFilter
    ) {
      dispatch(
        fetchAllEvents({
          filter: eventFilter,
          pageSize: 20,
          lastVisible: allEventsLastVisible,
          reset: false,
        }),
      );
    }
  }, [
    allEventsLoading,
    allEventsAllLoaded,
    allEventsLastVisible,
    eventFilter,
    currentFilter,
    dispatch,
  ]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadMore]);

  const handleFilterChange = (newFilter) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("filter", newFilter);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleSort = (field) => {
    const searchParams = new URLSearchParams(location.search);
    if (sortParams.sortBy === field) {
      // Toggle order
      const newOrder = sortParams.sortOrder === "asc" ? "desc" : "asc";
      searchParams.set("sortOrder", newOrder);
    } else {
      // New field, default to asc
      searchParams.set("sortBy", field);
      searchParams.set("sortOrder", "asc");
    }
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // Sort events (client-side for now, since we're filtering client-side)
  const sortedEvents = useMemo(() => {
    const sorted = [...filteredEvents];
    const { sortBy, sortOrder } = sortParams;

    sorted.sort((a, b) => {
      let aValue, bValue;

      if (sortBy === "name") {
        aValue = a.name || "Unnamed Event";
        bValue = b.name || "Unnamed Event";
      } else if (sortBy === "startsAt") {
        aValue = a.startsAt?.toMillis?.() || 0;
        bValue = b.startsAt?.toMillis?.() || 0;
      } else if (sortBy === "organizationId") {
        const aOrg = organizationsMap[a.organizationId]?.name || "";
        const bOrg = organizationsMap[b.organizationId]?.name || "";
        aValue = aOrg;
        bValue = bOrg;
      } else if (sortBy === "status") {
        aValue = getEventStatus(a);
        bValue = getEventStatus(b);
      } else if (sortBy === "createdAt") {
        aValue = a.createdAt?.toMillis?.() || 0;
        bValue = b.createdAt?.toMillis?.() || 0;
      } else {
        return 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [filteredEvents, sortParams, organizationsMap, getEventStatus]);

  // Handler for opening edit modal
  const handleOpenEditModal = (event) => {
    setEditingEvent(event);
    setModalHideFromPublicLists(event.hideFromPublicLists ?? false);
    setModalHideEvent(event.hideEvent ?? false);
    setEditModalOpen(true);
  };

  // Handler for closing edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingEvent(null);
    setModalHideFromPublicLists(false);
    setModalHideEvent(false);
  };

  // Handler for saving changes
  const handleSaveOverrides = useCallback(async () => {
    if (!editingEvent) return;

    setEditingEventId(editingEvent.id);
    try {
      await dispatch(
        updateEventOverrides({
          eventId: editingEvent.id,
          hideFromPublicLists: modalHideFromPublicLists,
          hideEvent: modalHideEvent,
        }),
      ).unwrap();

      // Update the local Redux state with the new override values
      dispatch(
        updateEventOverridesInState({
          eventId: editingEvent.id,
          hideFromPublicLists: modalHideFromPublicLists,
          hideEvent: modalHideEvent,
        }),
      );

      handleCloseEditModal();
    } catch (error) {
      if (
        error?.code === "permission-denied" ||
        error?.message?.includes("PERMISSION_DENIED")
      ) {
        alert(
          "Permission denied. You must be a superadmin to update event overrides. Please check that your user account has superadmin privileges.",
        );
      } else {
        alert("Failed to update overrides. Please try again.");
      }
    } finally {
      setEditingEventId(null);
    }
  }, [
    dispatch,
    editingEvent,
    modalHideFromPublicLists,
    modalHideEvent,
  ]);

  return (
    <Container>
      <Typography variant="h2" paddingTop={"20px"}>
        Events
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginTop={4}
        marginBottom={2}
      >
        <Box>
          {activeFilterCount > 0 && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              alignItems="center"
            >
              <Chip
                label="Clear all"
                onClick={clearAllFilters}
                size="small"
                variant="outlined"
              />
              {Object.entries(filterParams.include || {}).map(
                ([field, values]) =>
                  values.map((value) => {
                    const displayValue = getDisplayValue(field, value);
                    const fieldLabel =
                      field === "organizationId"
                        ? "organization"
                        : field === "status"
                        ? "status"
                        : field;
                    return (
                      <Chip
                        key={`include-${field}-${value}`}
                        label={`${fieldLabel}: ${displayValue}`}
                        onClick={(e) =>
                          handleChipClick(e, field, value, "include")
                        }
                        onDelete={(e) => {
                          e.stopPropagation();
                          clearFilter(field, value, "include");
                        }}
                        color="success"
                        size="small"
                        icon={<FilterListIcon />}
                        sx={{ cursor: "pointer" }}
                      />
                    );
                  }),
              )}
              {Object.entries(filterParams.exclude || {}).map(
                ([field, values]) =>
                  values.map((value) => {
                    const displayValue = getDisplayValue(field, value);
                    const fieldLabel =
                      field === "organizationId"
                        ? "organization"
                        : field === "status"
                        ? "status"
                        : field;
                    return (
                      <Chip
                        key={`exclude-${field}-${value}`}
                        label={`${fieldLabel}: ${displayValue} (excluded)`}
                        onClick={(e) =>
                          handleChipClick(e, field, value, "exclude")
                        }
                        onDelete={(e) => {
                          e.stopPropagation();
                          clearFilter(field, value, "exclude");
                        }}
                        color="error"
                        size="small"
                        icon={<RemoveCircleIcon />}
                        sx={{ cursor: "pointer" }}
                      />
                    );
                  }),
              )}
            </Stack>
          )}
        </Box>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <Select
            id="event-filter"
            name="event-filter"
            value={eventFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
          >
            <MenuItem value="upcoming">Upcoming Events</MenuItem>
            <MenuItem value="draft">Draft Events</MenuItem>
            <MenuItem value="past">Past Events</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {allEventsLoading && sortedEvents.length === 0 && (
        <LinearProgress sx={{ marginTop: 2, marginBottom: 2 }} />
      )}

      {sortedEvents.length === 0 && !allEventsLoading && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" marginTop={2}>
            No events found
          </Typography>
        </Paper>
      )}

      {sortedEvents.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <TableContainer sx={{ marginTop: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "name"}
                      direction={
                        sortParams.sortBy === "name"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("name")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "organizationId"}
                      direction={
                        sortParams.sortBy === "organizationId"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("organizationId")}
                    >
                      Organization
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "status"}
                      direction={
                        sortParams.sortBy === "status"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("status")}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "startsAt"}
                      direction={
                        sortParams.sortBy === "startsAt"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("startsAt")}
                    >
                      Starts
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Tickets Sold</TableCell>
                  <TableCell>Revenue</TableCell>
                  {isSuperAdmin && <TableCell>Superadmin Overrides</TableCell>}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEvents.map((event) => {
                  const startsDate = event.startsAt?.toDate
                    ? format(event.startsAt.toDate(), "MMM d, yyyy h:mm a")
                    : "N/A";

                  // Get organization name
                  const org = organizationsMap[event.organizationId];
                  const orgName = org?.name || "Unknown Organization";

                  // Get event stats
                  const stats = allEventStats[event.id];
                  const totalSoldCount = stats?.sold || 0;
                  const totalCollected = stats?.totalUsdCents ?? 0;
                  const refundedUsdCents = stats?.refundedUsdCents ?? 0;
                  const netRevenueUsdCents = totalCollected - refundedUsdCents;

                  // Format revenue amount (net of refunds)
                  let revenueText = "";
                  if (totalCollected !== undefined || refundedUsdCents > 0) {
                    revenueText = new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(netRevenueUsdCents / 100);
                  }

                  // Generate public URL if event has a name
                  const publicUrl = event.name
                    ? `/events/${getSlug(event.name)}-tickets-${event.id}`
                    : null;

                  const eventStatus = getEventStatus(event);

                  return (
                    <TableRow key={event.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {event.name || "Unnamed Event"}
                        </Typography>
                      </TableCell>
                      <FilterableCell
                        field="organizationId"
                        value={orgName}
                        rawValue={event.organizationId}
                      >
                        <Typography
                          variant="body2"
                          component={Link}
                          to={`/crm/organizations/${event.organizationId}`}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {orgName}
                        </Typography>
                      </FilterableCell>
                      <FilterableCell
                        field="status"
                        value={getDisplayValue("status", eventStatus)}
                        rawValue={eventStatus}
                      >
                        <EventStatusView
                          status={eventStatus}
                          squareLocationError={event.squareLocationError}
                        />
                      </FilterableCell>
                      <TableCell>
                        <Typography variant="body2">{startsDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {totalSoldCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {revenueText || "N/A"}
                        </Typography>
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">
                              {event.hideFromPublicLists === true
                                ? "Hidden from lists"
                                : ""}
                              {event.hideFromPublicLists === true &&
                              event.hideEvent === true
                                ? " | "
                                : ""}
                              {event.hideEvent === true ? "Event Hidden" : ""}
                            </Typography>
                            <Tooltip title="Edit superadmin overrides">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditModal(event)}
                                disabled={editingEventId === event.id}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        {publicUrl ? (
                          <Box display="flex" gap={1}>
                            <Tooltip title="View public event page">
                              <IconButton
                                component={Link}
                                to={publicUrl}
                                target="_blank"
                                size="small"
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy public URL">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    window.location.origin + publicUrl,
                                  );
                                }}
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Loading indicator and observer target for infinite scroll */}
          {allEventsLoading && sortedEvents.length > 0 && (
            <Box marginTop={2}>
              <LinearProgress />
            </Box>
          )}

          {/* Observer target for infinite scroll */}
          {!allEventsAllLoaded && (
            <div ref={observerTarget} style={{ height: "20px" }} />
          )}

          {allEventsAllLoaded && sortedEvents.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              marginTop={2}
              textAlign="center"
            >
              All events loaded
            </Typography>
          )}
        </Paper>
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleFilterBy}>
          <ListItemIcon>
            <FilterListIcon fontSize="small" sx={{ color: "success.main" }} />
          </ListItemIcon>
          <ListItemText>Filter by this value</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExclude}>
          <ListItemIcon>
            <RemoveCircleIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText>Exclude this value</ListItemText>
        </MenuItem>
      </Menu>

      {/* Chip Context Menu */}
      <Menu
        open={chipContextMenu !== null}
        onClose={handleChipContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          chipContextMenu !== null
            ? { top: chipContextMenu.mouseY, left: chipContextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleFlipFilter}>
          <ListItemIcon>
            {chipContextMenuData?.currentType === "include" ? (
              <RemoveCircleIcon fontSize="small" sx={{ color: "error.main" }} />
            ) : (
              <FilterListIcon fontSize="small" sx={{ color: "success.main" }} />
            )}
          </ListItemIcon>
          <ListItemText>
            {chipContextMenuData?.currentType === "include"
              ? "Switch to exclude"
              : "Switch to include"}
          </ListItemText>
        </MenuItem>
      </Menu>

      <EventOverridesModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        event={editingEvent}
        hideFromPublicLists={modalHideFromPublicLists}
        hideEvent={modalHideEvent}
        onHideFromPublicListsChange={setModalHideFromPublicLists}
        onHideEventChange={setModalHideEvent}
        onSave={handleSaveOverrides}
        onCancel={handleCloseEditModal}
        saving={editingEventId !== null}
      />
    </Container>
  );
}
